import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import Loading from '../../components/common/Loading';
import MobileAdminNav from '../../components/common/MobileAdminNav';
import {
  FaUsers,
  FaBox,
  FaBuilding,
  FaTruck,
  FaFileInvoice,
  FaQuoteLeft,
  FaUserTie,
  FaChartLine,
  FaSignOutAlt,
  FaUndo,
  FaCogs
} from 'react-icons/fa';
import './Dashboard.css';
import logo from '../../assets/img.jpg';

const Dashboard = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: dashboardData, isLoading, error, refetch } = useQuery(
    'adminDashboard',
    () => api.get('/api/admin/dashboard'),
    {
      staleTime: 2 * 60 * 1000, // 2 minutes
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: true, // Refetch when component mounts (e.g., when navigating back from Vendors page)
    }
  );

  const [modalType, setModalType] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [returnCount, setReturnCount] = useState(0);
  const [manufacturingCategoryCount, setManufacturingCategoryCount] = useState(0);
  const [ledgerStats, setLedgerStats] = useState({ totalEntries: 0, lastBalance: 0 });

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Load count of returns and manufacturing categories for dashboard cards
  React.useEffect(() => {
    const loadCounts = async () => {
      try {
        // Load returns count
        const returnsRes = await api.get('/api/returns');
        const returnsList = returnsRes.data?.returns || [];
        setReturnCount(Array.isArray(returnsList) ? returnsList.length : 0);

        // Load manufacturing categories count
        const categoriesRes = await api.get('/api/manufacturing-categories');
        const categoriesList = categoriesRes.data?.manufacturingCategories || [];
        setManufacturingCategoryCount(Array.isArray(categoriesList) ? categoriesList.length : 0);
      } catch (err) {
        setReturnCount(0);
        setManufacturingCategoryCount(0);
      }
      try {
        const statsRes = await api.get('/api/accounts-ledger/stats');
        const s = statsRes.data?.stats || {};
        setLedgerStats({ totalEntries: s.totalEntries || 0, lastBalance: s.lastBalance || 0 });
      } catch (e) {
        setLedgerStats({ totalEntries: 0, lastBalance: 0 });
      }
    };
    loadCounts();
  }, []);

  // Refetch dashboard data when component becomes visible and query is stale
  // This ensures vendor count updates immediately when navigating back from Vendors page
  React.useEffect(() => {
    const handleFocus = () => {
      // Only refetch if query is stale (was invalidated by Vendors page)
      const queryState = queryClient.getQueryState('adminDashboard');
      if (queryState?.isStale) {
        refetch();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [refetch, queryClient]);

  // Fetch data for modal when a card is clicked
  const handleViewClick = async (type) => {
    setModalType(type);
    setShowModal(true);
    setModalLoading(true);
    setModalError(null);
    let url = '';
    let key = '';

    if (type === 'users') {
      url = '/api/admin/users';
      key = 'users';
    } else if (type === 'orders') {
      url = '/api/admin/orders';
      key = 'orders';
    } else if (type === 'products') {
      url = '/api/admin/products';
      key = 'products';
    } else if (type === 'companies') {
      url = '/api/companies';
      key = 'companies';
    } else if (type === 'vendors') {
      url = '/api/vendors';
      key = 'vendors';
    } else if (type === 'inventory') {
      url = '/api/inventory';
      key = 'inventoryItems';
    } else if (type === 'billings') {
      url = '/api/billing';
      key = 'billings';
    } else if (type === 'quotations') {
      url = '/api/quotations';
      key = 'quotations';
    } else if (type === 'accounts') {
      url = '/api/accounts';
      key = 'accounts';
    } else if (type === 'customers') {
      url = '/api/customers';
      key = 'customers';
    } else if (type === 'returns') {
      url = '/api/returns';
      key = 'returns';
    } else if (type === 'manufacturing-categories') {
      url = '/api/manufacturing-categories';
      key = 'manufacturingCategories';
    } else if (type === 'accounts-ledger') {
      url = '/api/accounts-ledger';
      key = 'ledgers';
    } else {
      return;
    }

    try {
      const res = await api.get(url);
      setModalData(res.data[key] || res.data);
    } catch (err) {
      setModalError('Failed to fetch data.');
      setModalData([]);
    } finally {
      setModalLoading(false);
    }
  };

  const stats = dashboardData?.data?.stats || {};
  const recentBillings = dashboardData?.data?.recentBillings || [];
  const lowStockItems = dashboardData?.data?.lowStockItems || [];

  // Modal content for each card
  const renderModalContent = () => {
    if (modalLoading) return <Loading />;
    if (modalError) return <div style={{ color: 'red' }}>{modalError}</div>;
    if (modalType === 'users') {
      return (
        <div>
          <h2>All Users</h2>
          {modalData.length === 0 ? <p>No users found.</p> : (
            <table className="modal-table"><thead><tr><th>Name</th><th>Email</th><th>Role</th></tr></thead><tbody>
              {modalData.map(user => (
                <tr key={user._id}><td>{user.name}</td><td>{user.email}</td><td>{user.role}</td></tr>
              ))}
            </tbody></table>
          )}
        </div>
      );
    }
    if (modalType === 'orders') {
      return (
        <div>
          <h2>All Orders</h2>
          {modalData.length === 0 ? <p>No orders found.</p> : (
            <table className="modal-table"><thead><tr><th>Order ID</th><th>User</th><th>Status</th><th>Total</th></tr></thead><tbody>
              {modalData.map(order => (
                <tr key={order._id}><td>{order._id.slice(-8).toUpperCase()}</td><td>{order.user?.name || 'N/A'}</td><td>{order.status}</td><td>${order.totalAmount?.toFixed(2)}</td></tr>
              ))}
            </tbody></table>
          )}
        </div>
      );
    }
    if (modalType === 'products') {
      return (
        <div>
          <h2>All Products</h2>
          {modalData.length === 0 ? <p>No products found.</p> : (
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Price</th>
                  <th>Stock</th>
                  <th>Category</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {modalData.map(product => (
                  <tr key={product._id}>
                    <td>{product.name}</td>
                    <td>${product.price?.toFixed(2)}</td>
                    <td>{product.stock}</td>
                    <td>{product.category}</td>
                    <td>{product.status || 'active'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }
    if (modalType === 'companies') {
      return (
        <div>
          <h2>All Companies</h2>
          {modalData.length === 0 ? <p>No companies found.</p> : (
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Contact Person</th>
                  <th>Email</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {modalData.map(company => (
                  <tr key={company._id}>
                    <td>{company.name}</td>
                    <td>{company.assignedTo?.name || company.contactPerson || 'N/A'}</td>
                    <td>{company.contact?.email || company.contactEmail || 'N/A'}</td>
                    <td>{company.status || 'active'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }
    if (modalType === 'vendors') {
      return (
        <div>
          <h2>All Vendors</h2>
          {modalData.length === 0 ? <p>No vendors found.</p> : (
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Contact Person</th>
                  <th>Email</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {modalData.map(vendor => (
                  <tr key={vendor._id}>
                    <td>{vendor.name}</td>
                    <td>{vendor.assignedTo?.name || vendor.contactPerson || 'N/A'}</td>
                    <td>{vendor.contact?.email || vendor.contactEmail || 'N/A'}</td>
                    <td>{vendor.status || 'active'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }
    if (modalType === 'inventory') {
      return (
        <div>
          <h2>All Inventory Items</h2>
          {modalData.length === 0 ? <p>No inventory items found.</p> : (
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Item Name</th>
                  <th>Item Code</th>
                  <th>Quantity</th>
                  <th>Selling Price</th>
                  <th>Company</th>
                </tr>
              </thead>
              <tbody>
                {modalData.map(item => (
                  <tr key={item._id}>
                    <td>{item.itemName}</td>
                    <td>{item.itemCode}</td>
                    <td>{item.itemQuantity}</td>
                    <td>${item.sellingPrice?.toFixed(2)}</td>
                    <td>{item.company?.name || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }
    if (modalType === 'billings') {
      return (
        <div>
          <h2>All Billings</h2>
          {modalData.length === 0 ? <p>No billings found.</p> : (
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Billing No</th>
                  <th>Company</th>
                  <th>Total Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {modalData.map(billing => (
                  <tr key={billing._id}>
                    <td>{billing.billingNo}</td>
                    <td>{billing.company?.name || 'N/A'}</td>
                    <td>${billing.totalAmount?.toFixed(2)}</td>
                    <td>{billing.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }
    if (modalType === 'quotations') {
      return (
        <div>
          <h2>All Quotations</h2>
          {modalData.length === 0 ? <p>No quotations found.</p> : (
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Quotation No</th>
                  <th>Company</th>
                  <th>Total Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {modalData.map(quotation => (
                  <tr key={quotation._id}>
                    <td>{quotation.quotationNo}</td>
                    <td>{quotation.company?.name || 'N/A'}</td>
                    <td>${quotation.totalAmount?.toFixed(2)}</td>
                    <td>{quotation.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }
    if (modalType === 'accounts') {
      return (
        <div>
          <h2>All Accounts</h2>
          {modalData.length === 0 ? <p>No accounts found.</p> : (
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Account Code</th>
                  <th>Account Name</th>
                  <th>Type</th>
                  <th>Current Balance</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {modalData.map(account => (
                  <tr key={account._id}>
                    <td>{account.accountCode}</td>
                    <td>{account.accountName}</td>
                    <td>{account.accountType}</td>
                    <td>${account.currentBalance?.toFixed(2)}</td>
                    <td>{account.isActive ? 'Active' : 'Inactive'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }
    if (modalType === 'customers') {
      return (
        <div>
          <h2>All Customers</h2>
          {modalData.length === 0 ? <p>No customers found.</p> : (
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Customer Code</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Type</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {modalData.map(customer => (
                  <tr key={customer._id}>
                    <td>{customer.customerCode}</td>
                    <td>{customer.firstName} {customer.lastName}</td>
                    <td>{customer.email}</td>
                    <td>{customer.customerType}</td>
                    <td>{customer.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }
    if (modalType === 'returns') {
      return (
        <div>
          <h2>Returned Products</h2>
          {modalData.length === 0 ? <p>No returns found.</p> : (
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Item</th>
                  <th>Code</th>
                  <th>Qty</th>
                  <th>Condition</th>
                  <th>Reason</th>
                  <th>Received By</th>
                </tr>
              </thead>
              <tbody>
                {modalData.map(r => (
                  <tr key={r._id}>
                    <td>{new Date(r.createdAt).toLocaleString()}</td>
                    <td>{r.itemName}</td>
                    <td>{r.itemCode}</td>
                    <td>{r.quantity}</td>
                    <td>{r.condition}</td>
                    <td>{r.reason || '-'}</td>
                    <td>{r.receivedBy?.name || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }
    if (modalType === 'manufacturing-categories') {
      return (
        <div>
          <h2>Manufacturing Categories</h2>
          {modalData.length === 0 ? <p>No manufacturing categories found.</p> : (
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Created By</th>
                </tr>
              </thead>
              <tbody>
                {modalData.map(category => (
                  <tr key={category._id}>
                    <td>{category.name}</td>
                    <td>{category.code}</td>
                    <td>{category.description || '-'}</td>
                    <td>{category.status}</td>
                    <td>{category.createdBy?.name || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }
    if (modalType === 'revenue') {
      return (
        <div>
          <h2>Revenue Details</h2>
          <p>Total Revenue: ${stats.totalRevenue?.toFixed(2) || '0.00'}</p>
        </div>
      );
    }
    if (modalType === 'accounts-ledger') {
      return (
        <div>
          <h2>Accounts Ledger</h2>
          {modalData.length === 0 ? <p>No ledger entries found.</p> : (
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Account</th>
                  <th>Description</th>
                  <th>Debit</th>
                  <th>Credit</th>
                  <th>Balance</th>
                </tr>
              </thead>
              <tbody>
                {modalData.map(entry => (
                  <tr key={entry._id}>
                    <td>{entry.date ? new Date(entry.date).toLocaleString() : ''}</td>
                    <td>{entry.account?.accountName || '-'}</td>
                    <td>{entry.description || '-'}</td>
                    <td>₹{Number(entry.debit || 0).toFixed(2)}</td>
                    <td>₹{Number(entry.credit || 0).toFixed(2)}</td>
                    <td>₹{Number(entry.runningBalance || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="dashboard-container">
      {/* Mobile Navigation */}
      <MobileAdminNav />

      {/* Desktop Sidebar */}
      <aside className="dashboard-sidebar">
        <img src={logo} alt="AVS Business Logo" className="sidebar-logo" />
        <h2>Admin Menu</h2>
        <nav>
          <ul>
            <li>
              <Link to="/adminavsbusiness.com/companies"><FaBuilding style={{ marginRight: '10px', verticalAlign: 'middle' }} />Companies</Link>
            </li>
            <li>
              <Link to="/adminavsbusiness.com/vendors"><FaTruck style={{ marginRight: '10px', verticalAlign: 'middle' }} />Vendors</Link>
            </li>
            <li>
              <Link to="/adminavsbusiness.com/inventory"><FaBox style={{ marginRight: '10px', verticalAlign: 'middle' }} />Inventory</Link>
            </li>
            <li>
              <Link to="/adminavsbusiness.com/billing"><FaFileInvoice style={{ marginRight: '10px', verticalAlign: 'middle' }} />Billing</Link>
            </li>
            <li>
              <Link to="/adminavsbusiness.com/quotations"><FaQuoteLeft style={{ marginRight: '10px', verticalAlign: 'middle' }} />Quotations</Link>
            </li>
            <li>
              <Link to="/adminavsbusiness.com/returns"><FaUndo style={{ marginRight: '10px', verticalAlign: 'middle' }} />Return Product</Link>
            </li>
            <li>
              <Link to="/adminavsbusiness.com/accounts"><FaChartLine style={{ marginRight: '10px', verticalAlign: 'middle' }} />Accounts</Link>
            </li>
            <li>
              <Link to="/adminavsbusiness.com/accounts-ledger"><FaChartLine style={{ marginRight: '10px', verticalAlign: 'middle' }} />Accounts Ledger</Link>
            </li>
            <li>
              <Link to="/adminavsbusiness.com/client-ledger"><FaChartLine style={{ marginRight: '10px', verticalAlign: 'middle' }} />Client Ledger</Link>
            </li>
            <li>
              <Link to="/adminavsbusiness.com/customers"><FaUserTie style={{ marginRight: '10px', verticalAlign: 'middle' }} />Customers</Link>
            </li>
            <li>
              <Link to="/adminavsbusiness.com/manufacturing-categories"><FaCogs style={{ marginRight: '10px', verticalAlign: 'middle' }} />Manufacturing Categories</Link>
            </li>
            <li>
              <Link to="/adminavsbusiness.com/users"><FaUsers style={{ marginRight: '10px', verticalAlign: 'middle' }} />Users</Link>
            </li>
          </ul>
        </nav>
        <div className="sidebar-logout">
          <button
            onClick={handleLogout}
            className="logout-button"
            style={{
              background: 'linear-gradient(135deg,#ef4444,#f97316)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 20px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
              justifyContent: 'center',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              boxShadow: '0 4px 12px rgba(239,68,68,0.3)'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(239,68,68,0.4)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(239,68,68,0.3)';
            }}
          >
            <FaSignOutAlt />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        <h1>AVS BUSINESS Dashboard</h1>
        <p>Overview of your AVS BUSINESS management system</p>

        {isLoading && <Loading />}
        {error && (
          <div className="error-message" style={{
            padding: '20px',
            backgroundColor: '#fee',
            color: '#c33',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            Error loading dashboard data: {error.response?.data?.message || error.message || 'Unknown error'}
          </div>
        )}

        {/* Stats Cards */}
        <div className="dashboard-cards">
          <div className="dashboard-card">
            <div className="card-title">Companies</div>
            <div className="card-value">{stats.totalCompanies || 0}</div>
            <button className="card-link" onClick={() => handleViewClick('companies')}>View</button>
          </div>
          <div className="dashboard-card">
            <div className="card-title">Vendors</div>
            <div className="card-value">{stats.totalVendors || 0}</div>
            <button className="card-link" onClick={() => handleViewClick('vendors')}>View</button>
          </div>
          <div className="dashboard-card">
            <div className="card-title">Inventory Items</div>
            <div className="card-value">{stats.totalInventoryItems || 0}</div>
            <button className="card-link" onClick={() => handleViewClick('inventory')}>View</button>
          </div>
          <div className="dashboard-card">
            <div className="card-title">Total Billings</div>
            <div className="card-value">{stats.totalBillings || 0}</div>
            <button className="card-link" onClick={() => handleViewClick('billings')}>View</button>
          </div>
          <div className="dashboard-card">
            <div className="card-title">Quotations</div>
            <div className="card-value">{stats.totalQuotations || 0}</div>
            <button className="card-link" onClick={() => handleViewClick('quotations')}>View</button>
          </div>
          <div className="dashboard-card">
            <div className="card-title">Accounts</div>
            <div className="card-value">{stats.totalAccounts || 0}</div>
            <button className="card-link" onClick={() => handleViewClick('accounts')}>View</button>
          </div>
          <div className="dashboard-card" role="button" tabIndex={0} onClick={() => handleViewClick('accounts-ledger')} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleViewClick('accounts-ledger'); } }}>
            <div className="card-title">Accounts Ledger</div>
            <div className="card-value">{ledgerStats.totalEntries}</div>
            <button className="card-link" type="button">View</button>
          </div>
          <div className="dashboard-card">
            <div className="card-title">Customers</div>
            <div className="card-value">{stats.totalCustomers || 0}</div>
            <button className="card-link" onClick={() => handleViewClick('customers')}>View</button>
          </div>
          <div className="dashboard-card">
            <div className="card-title">Return Product</div>
            <div className="card-value">{returnCount}</div>
            <button className="card-link" onClick={() => handleViewClick('returns')}>View</button>
          </div>
          <div className="dashboard-card">
            <div className="card-title">Manufacturing Categories</div>
            <div className="card-value">{manufacturingCategoryCount}</div>
            <button className="card-link" onClick={() => handleViewClick('manufacturing-categories')}>View</button>
          </div>
        </div>

        <div className="dashboard-section">
          <h2>Recent Billings</h2>
          <div className="section-content">
            {(recentBillings.length === 0) ? (
              <p>No recent billings</p>
            ) : (
              <div>
                {recentBillings.slice(0, 5).map((billing) => (
                  <div key={billing._id}>
                    {billing.billingNo} - {billing.company?.name || 'Unknown Company'} - ₹{billing.totalAmount?.toFixed(2)}
                  </div>
                ))}
              </div>
            )}
            <Link to="/adminavsbusiness.com/billing" className="section-link">
              View All Billings
            </Link>
          </div>
        </div>

        <div className="dashboard-section">
          <h2>Low Stock Items</h2>
          <div className="section-content">
            {(lowStockItems.length === 0) ? (
              <p>All inventory items have sufficient stock</p>
            ) : (
              <div>
                {lowStockItems.slice(0, 5).map((item) => (
                  <div key={item._id}>
                    {item.itemName} ({item.itemCode}) - Stock: {item.itemQuantity} - {item.company?.name}
                  </div>
                ))}
              </div>
            )}
            <Link to="/adminavsbusiness.com/inventory" className="section-link">
              Manage Inventory
            </Link>
          </div>
        </div>

        {/* Modal for details */}
        {showModal && (
          <div className="dashboard-modal-overlay" onClick={() => setShowModal(false)}>
            <div className="dashboard-modal" onClick={e => e.stopPropagation()}>
              <button className="dashboard-modal-close" onClick={() => setShowModal(false)}>&times;</button>
              {renderModalContent()}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard; 